import { z } from 'zod';
export const baseQuantitySchema = z
  .string()
  .regex(/^(?:0|[1-9]\d{0,17})(?:\.\d{1,6})?$/)
  .refine((v) => /[1-9]/.test(v));
const unit = z.enum(['g', 'ml', 'ea']);
const evidence = z.string().min(1).max(4000);
const id = z.uuid();
const quantity = { quantity: baseQuantitySchema, unit, evidence };
const owner = { ownerCustomerId: id.nullable() };
export const operationsPayloadSchema = z
  .discriminatedUnion('action', [
    z
      .object({
        action: z.literal('purchase_draft'),
        ...owner,
        specificationId: id,
        supplierReference: z.string().min(1).max(1000),
        ...quantity,
      })
      .strict(),
    z
      .object({
        action: z.literal('receive'),
        ...owner,
        specificationId: id,
        purchaseDraftId: id.nullable(),
        supplierReference: z.string().min(1).max(1000),
        receiptReference: z.string().min(1).max(1000),
        label: z.string().min(1).max(200),
        facility: z.string().min(1).max(200),
        location: z.string().min(1).max(200),
        expiresAt: z.iso.datetime({ offset: true }),
        ...quantity,
      })
      .strict(),
    z
      .object({
        action: z.literal('lot_qa'),
        lotId: id,
        disposition: z.enum(['accepted', 'rejected', 'quarantine']),
        evidence,
      })
      .strict(),
    z.object({ action: z.literal('reserve'), lotId: id, orderId: id, ...quantity }).strict(),
    z
      .object({ action: z.literal('unreserve'), lotId: id, reservationId: id, ...quantity })
      .strict(),
    ...(['correct_in', 'correct_out'] as const).map((action) =>
      z
        .object({ action: z.literal(action), lotId: id, referenceEventId: id, ...quantity })
        .strict(),
    ),
    ...(['damage', 'return_supplier'] as const).map((action) =>
      z.object({ action: z.literal(action), lotId: id, ...quantity }).strict(),
    ),
  ])
  .superRefine((v, c) => {
    if ('unit' in v && v.unit === 'ea' && v.quantity.includes('.') && Number(v.quantity) % 1 !== 0)
      c.addIssue({ code: 'custom', message: 'whole_count_required' });
  });
export type OperationsCommand = z.infer<typeof operationsPayloadSchema>;
export const operationsRequestSchema = z
  .object({
    organizationId: id,
    projectId: id.nullable(),
    idempotencyKey: id,
    payload: operationsPayloadSchema,
  })
  .strict();
export const operationsWorkspaceSchema = z.object({
  specifications: z.array(
    z.object({
      id,
      hash: z.string().regex(/^[a-f0-9]{64}$/),
      kind: z.enum(['material_spec', 'component_spec']),
      label: z.string(),
    }),
  ),
  grants: z.array(
    z.object({
      capability: z.string(),
      customerId: id.nullable(),
      projectId: id.nullable(),
      facility: z.string().nullable(),
      validUntil: z.iso.datetime({ offset: true }),
    }),
  ),
  lots: z.array(
    z.object({
      id,
      ownerCustomerId: id.nullable(),
      facility: z.string(),
      location: z.string(),
      label: z.string(),
      specificationId: id,
      specificationHash: z.string(),
      receiptReference: z.string(),
      expiresAt: z.iso.datetime({ offset: true }),
      unit,
      qaState: z.enum(['quarantine', 'accepted', 'rejected']),
      balance: z.object({ onHand: z.string(), reserved: z.string() }),
      events: z.array(
        z
          .object({
            id,
            event: z.string(),
            quantity: z.union([z.number(), z.string()]),
            reservation_id: id.nullable(),
          })
          .loose(),
      ),
    }),
  ),
  purchaseDrafts: z.array(
    z.object({
      id,
      ownerCustomerId: id.nullable(),
      specificationId: id,
      quantity: z.string(),
      unit,
      state: z.literal('draft_no_external_order'),
    }),
  ),
});
export type OperationsWorkspace = z.infer<typeof operationsWorkspaceSchema>;
